import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send, Loader2, Video, Trash2, Plus, Mail, CheckCircle2, PlayCircle,
  CalendarClock, Users, ChevronDown, ChevronUp, MessagesSquare, BellRing,
} from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_admin_token";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const inputCls = "mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40";
const labelCls = "text-sm font-medium text-navy";
const eyebrowCls = "text-[11px] uppercase tracking-widest text-teal font-bold";

/* ---------------- Field Update composer ---------------- */
function FieldUpdateComposer() {
  const [recips, setRecips] = useState([]);
  const [checked, setChecked] = useState({});
  const [showRecips, setShowRecips] = useState(false);
  const [form, setForm] = useState({ subject: "", video_url: "", thumbnail_url: "", brief: "", message: "", script: "", confidence_note: "", action_item: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/field-updates/recipients").then((r) => {
      setRecips(r.data || []);
      const all = {};
      (r.data || []).forEach((x) => { all[x.email] = true; });
      setChecked(all);
    }).catch(() => {});
    api.get("/admin/field-updates").then((r) => setHistory(r.data || [])).catch(() => {});
    api.get("/admin/field-update-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => recips.filter((r) => checked[r.email]).map((r) => r.email), [recips, checked]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm({
      subject: t.subject || "",
      video_url: t.video_url || "",
      thumbnail_url: t.thumbnail_url || "",
      brief: t.brief || "",
      message: t.message || "",
      script: t.script || "",
      confidence_note: t.confidence_note || "",
      action_item: t.action_item || "",
    });
    toast.success(`Loaded template "${t.name}"`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this template (e.g. 'Rate checkup theme'):");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      const { data } = await api.post("/admin/field-update-templates", { name: name.trim(), ...form });
      setTemplates((prev) => [data, ...prev]);
      toast.success("Template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template.");
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/admin/field-update-templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Could not delete template.");
    }
  };

  const send = async () => {
    if (!form.message.trim() || !form.script.trim() || !form.action_item.trim()) {
      toast.error("Message, outreach script, and action item are required.");
      return;
    }
    if (!form.brief.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Add the written brief — every send goes out with the video and the writing.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one recipient.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/field-updates/send", { ...form, recipients: selected });
      toast.success(`Field Update sent to ${data.sent_count} partner${data.sent_count !== 1 ? "s" : ""}`);
      setForm({ subject: "", video_url: "", thumbnail_url: "", brief: "", message: "", script: "", confidence_note: "", action_item: "" });
      api.get("/admin/field-updates").then((r) => setHistory(r.data || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-field-update">
      <div className="flex items-center gap-2">
        <PlayCircle size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Weekly Field Update</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">The 3-minute weekly brief for all MLOs and approved partners — video, message of the week, copy-paste script, and one action item.</p>

      {/* Templates */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="fu-templates">
        <span className="text-xs font-medium text-ink-500">Templates:</span>
        {templates.length === 0 && <span className="text-xs text-ink-400">none saved yet</span>}
        {templates.map((t) => (
          <span key={t.id} className="inline-flex items-center rounded-full bg-linen border border-linen-border text-xs">
            <button onClick={() => loadTemplate(t.id)} data-testid={`fu-template-load-${t.id}`} className="pl-3 pr-1.5 py-1.5 font-medium text-navy hover:text-teal transition-colors">
              {t.name}
            </button>
            <button onClick={() => deleteTemplate(t.id)} data-testid={`fu-template-delete-${t.id}`} className="pr-2.5 pl-0.5 py-1.5 text-ink-400 hover:text-red-600" aria-label={`Delete template ${t.name}`}>
              ×
            </button>
          </span>
        ))}
        <button onClick={saveTemplate} disabled={savingTpl} data-testid="fu-template-save" className="inline-flex items-center gap-1 rounded-full border border-dashed border-teal/50 text-teal text-xs font-medium px-3 py-1.5 hover:bg-teal-light/50 transition-colors">
          + Save current as template
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Subject (optional — auto-fills with the week)</span>
          <input data-testid="fu-subject" value={form.subject} onChange={set("subject")} placeholder="The Field Update — Week of…" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Video link (YouTube, Loom, Zoom — any URL)</span>
          <input data-testid="fu-video" value={form.video_url} onChange={set("video_url")} placeholder="https://…" className={inputCls} />
        </label>
      </div>
      <label className="block mt-4">
        <span className={labelCls}>Thumbnail image URL (optional)</span>
        <span className="mt-0.5 block text-[11px] text-ink-500">
          Leave blank and we pull the video's own still frame — or fall back to the branded Field Update
          poster. Paste an image URL here to override it.
        </span>
        <input data-testid="fu-thumbnail" value={form.thumbnail_url} onChange={set("thumbnail_url")} placeholder="https://…/frame.jpg" className={inputCls} />
        {!!form.thumbnail_url.trim() && (
          <img
            src={form.thumbnail_url}
            alt="Thumbnail preview"
            data-testid="fu-thumbnail-preview"
            className="mt-2 w-full max-w-xs rounded-xl border border-linen-border object-cover aspect-video"
          />
        )}
      </label>
      <label className="block mt-4">
        <span className={labelCls}>The written brief *</span>
        <span className="mt-0.5 block text-[11px] text-ink-500">
          The same update in writing — for the partners who won't press play. Goes into the email body and
          onto the update's own web page, which is safe to forward.
        </span>
        <RichTextEditor
          value={form.brief}
          onChange={(html) => setForm((f) => ({ ...f, brief: html }))}
          placeholder="What happened this week, what it means for them, and what changed…"
          testid="fu-brief"
        />
      </label>
      <label className="block mt-4">
        <span className={labelCls}>This week's message *</span>
        <textarea data-testid="fu-message" value={form.message} onChange={set("message")} rows={3} placeholder="The one talking point partners should lead with this week…" className={inputCls} />
      </label>
      <label className="block mt-4">
        <span className={labelCls}>Copy / paste outreach script *</span>
        <textarea data-testid="fu-script" value={form.script} onChange={set("script")} rows={4} placeholder={"Hey [Name]! Quick thought — …"} className={inputCls} />
      </label>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Confidence check (optional)</span>
          <input data-testid="fu-confidence" value={form.confidence_note} onChange={set("confidence_note")} placeholder="Reminder: no rate promises — licensed pros handle the numbers." className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>This week's action item *</span>
          <input data-testid="fu-action" value={form.action_item} onChange={set("action_item")} placeholder="Text five past clients the rate-checkup link." className={inputCls} />
        </label>
      </div>

      {/* Recipients */}
      <div className="mt-5 rounded-xl border border-linen-border bg-linen/60 p-4">
        <button onClick={() => setShowRecips((s) => !s)} data-testid="fu-recipients-toggle" className="w-full flex items-center justify-between text-left">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-navy">
            <Users size={15} className="text-teal" /> Recipients: {selected.length} of {recips.length} selected
          </span>
          {showRecips ? <ChevronUp size={16} className="text-ink-500" /> : <ChevronDown size={16} className="text-ink-500" />}
        </button>
        {showRecips && (
          <div className="mt-3">
            <div className="flex gap-3 text-xs mb-2">
              <button data-testid="fu-select-all" onClick={() => { const a = {}; recips.forEach((r) => { a[r.email] = true; }); setChecked(a); }} className="text-teal font-medium">Select all</button>
              <button data-testid="fu-clear-all" onClick={() => setChecked({})} className="text-ink-500">Clear</button>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
              {recips.map((r) => (
                <label key={r.email} className="flex items-center gap-2.5 rounded-lg bg-white border border-linen-border px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!checked[r.email]} onChange={() => setChecked((c) => ({ ...c, [r.email]: !c[r.email] }))} className="accent-[#1F8F8B]" />
                  <span className="text-sm text-navy">{r.name || r.email}</span>
                  <span className="text-[11px] text-ink-400">{r.email}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.role === "MLO" ? "bg-teal-light text-teal" : "bg-lime-soft text-navy"}`}>{r.role}</span>
                </label>
              ))}
              {recips.length === 0 && <div className="text-xs text-ink-500 py-2">No active MLOs or approved partners with emails yet.</div>}
            </div>
          </div>
        )}
      </div>

      <button onClick={send} disabled={sending} data-testid="fu-send" className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold px-6 py-3 transition-colors">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        Send Field Update to {selected.length} partner{selected.length !== 1 ? "s" : ""}
      </button>

      {history.length > 0 && (
        <div className="mt-6 border-t border-linen-border pt-4">
          <div className={eyebrowCls}>Past sends</div>
          <div className="mt-2 space-y-1.5" data-testid="fu-history">
            {history.slice(0, 8).map((h) => (
              <PastSendRow
                key={h.id}
                entry={h}
                recips={recips}
                onLoad={() => {
                  setForm({
                    subject: h.subject || "",
                    video_url: h.video_url || "",
                    thumbnail_url: h.thumbnail_url || "",
                    brief: h.brief || "",
                    message: h.message || "",
                    script: h.script || "",
                    confidence_note: h.confidence_note || "",
                    action_item: h.action_item || "",
                  });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  toast.success("Loaded into the composer — edit and send as a new update.");
                }}
                onResent={() => api.get("/admin/field-updates").then((r) => setHistory(r.data || []))}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* One row in "Past sends" — resend to more people, or pull it back into the composer. */
function PastSendRow({ entry, recips, onLoad, onResent }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState({});
  const [sending, setSending] = useState(false);
  const already = entry.already_sent_to || entry.recipients || [];

  const openPicker = () => {
    const next = {};
    recips.forEach((r) => { next[r.email] = !already.includes(r.email); });
    setChecked(next);
    setOpen((s) => !s);
  };
  const selected = recips.filter((r) => checked[r.email]).map((r) => r.email);

  const resend = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one recipient.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/field-updates/${entry.id}/resend`, { recipients: selected });
      toast.success(`Resent to ${data.sent_count} partner${data.sent_count !== 1 ? "s" : ""}`);
      setOpen(false);
      onResent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg bg-linen/70 px-3 py-2" data-testid={`fu-history-row-${entry.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <Mail size={13} className="text-teal shrink-0" />
        <span className="font-medium text-navy">{entry.subject}</span>
        {entry.resend_of && (
          <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-teal-light text-teal px-2 py-0.5">Resend</span>
        )}
        <span className="text-ink-500">→ {entry.sent_count} recipient{entry.sent_count !== 1 ? "s" : ""}</span>
        {entry.tracked_count > 0 && (
          <span data-testid={`fu-opens-${entry.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-teal-light text-teal px-2 py-0.5">
            {entry.open_count} of {entry.tracked_count} opened
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-[11px]">
          <button onClick={openPicker} data-testid={`fu-resend-toggle-${entry.id}`} className="font-semibold text-teal hover:text-navy transition-colors">
            {open ? "Cancel" : "Resend →"}
          </button>
          <button onClick={onLoad} data-testid={`fu-load-${entry.id}`} className="text-ink-500 hover:text-navy transition-colors">
            Load into composer
          </button>
          <span className="text-ink-400">{(entry.created_at || "").slice(0, 10)}</span>
        </span>
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-linen-border bg-white p-3" data-testid={`fu-resend-panel-${entry.id}`}>
          <p className="text-[11px] text-ink-500">
            Same email, same web page. Anyone who already received it starts unchecked — tick them to send again.
          </p>
          <div className="flex gap-3 text-xs my-2">
            <button
              data-testid={`fu-resend-select-new-${entry.id}`}
              onClick={() => { const n = {}; recips.forEach((r) => { n[r.email] = !already.includes(r.email); }); setChecked(n); }}
              className="text-teal font-medium"
            >
              Select everyone new
            </button>
            <button data-testid={`fu-resend-select-all-${entry.id}`} onClick={() => { const n = {}; recips.forEach((r) => { n[r.email] = true; }); setChecked(n); }} className="text-teal font-medium">
              Select all
            </button>
            <button data-testid={`fu-resend-clear-${entry.id}`} onClick={() => setChecked({})} className="text-ink-500">Clear</button>
          </div>
          <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
            {recips.map((r) => (
              <label key={r.email} className="flex items-center gap-2.5 rounded-lg bg-linen/60 border border-linen-border px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`fu-resend-recipient-${entry.id}-${r.email}`}
                  checked={!!checked[r.email]}
                  onChange={() => setChecked((c) => ({ ...c, [r.email]: !c[r.email] }))}
                  className="accent-[#1F8F8B]"
                />
                <span className="text-sm text-navy">{r.name || r.email}</span>
                <span className="text-[11px] text-ink-400">{r.email}</span>
                {already.includes(r.email) && (
                  <span className="text-[10px] font-medium uppercase tracking-wider text-ink-400">already sent</span>
                )}
                {(entry.opened_by || []).includes(r.email) && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-teal">opened</span>
                )}
                <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.role === "MLO" ? "bg-teal-light text-teal" : "bg-lime-soft text-navy"}`}>{r.role}</span>
              </label>
            ))}
            {recips.length === 0 && <div className="text-xs text-ink-500 py-2">No active MLOs or approved partners with emails yet.</div>}
          </div>
          <button
            onClick={resend}
            disabled={sending}
            data-testid={`fu-resend-send-${entry.id}`}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
            Resend to {selected.length} partner{selected.length !== 1 ? "s" : ""}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Replay manager ---------------- */
function ReplayManager() {
  const [replays, setReplays] = useState([]);
  const [form, setForm] = useState({ title: "", video_url: "", kind: "meeting", recorded_on: "", duration_label: "", description: "" });
  const [notifyPartners, setNotifyPartners] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/replays").then((r) => setReplays(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.title.trim() || !form.video_url.trim()) {
      toast.error("Title and video URL are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/replays", { ...form, notify_partners: notifyPartners });
      if (data.notified_count > 0) {
        toast.success(`Replay added — ${data.notified_count} partner${data.notified_count !== 1 ? "s" : ""} notified by email.`);
      } else {
        toast.success("Replay added — partners can watch it now.");
      }
      setForm({ title: "", video_url: "", kind: "meeting", recorded_on: "", duration_label: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add replay.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/replays/${id}`);
      setReplays((prev) => prev.filter((r) => r.id !== id));
      toast.success("Replay removed.");
    } catch {
      toast.error("Could not remove replay.");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-replays">
      <div className="flex items-center gap-2">
        <Video size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Replay Library</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">Recorded monthly meetings and training clips. Visible to MLOs (in their Toolkit) and partners (in the Partner Library).</p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Title *</span>
          <input data-testid="replay-title" value={form.title} onChange={set("title")} placeholder="March Partner Growth Meeting" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Video URL *</span>
          <input data-testid="replay-url" value={form.video_url} onChange={set("video_url")} placeholder="https://youtube.com/… or Zoom link" className={inputCls} />
        </label>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block">
          <span className={labelCls}>Type</span>
          <select data-testid="replay-kind" value={form.kind} onChange={set("kind")} className={inputCls}>
            <option value="meeting">Monthly meeting</option>
            <option value="training">Training clip</option>
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Recorded on</span>
          <input data-testid="replay-date" type="date" value={form.recorded_on} onChange={set("recorded_on")} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Duration</span>
          <input data-testid="replay-duration" value={form.duration_label} onChange={set("duration_label")} placeholder="45 min" className={inputCls} />
        </label>
      </div>
      <label className="block mt-4">
        <span className={labelCls}>Description (optional)</span>
        <input data-testid="replay-desc" value={form.description} onChange={set("description")} placeholder="What partners will get out of watching this." className={inputCls} />
      </label>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button onClick={add} disabled={busy} data-testid="replay-add" className="inline-flex items-center gap-2 rounded-full bg-teal hover:bg-teal-hover disabled:opacity-50 text-white font-semibold px-5 py-2.5 text-sm transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add replay
        </button>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={notifyPartners} onChange={(e) => setNotifyPartners(e.target.checked)} data-testid="replay-notify-toggle" className="accent-teal" />
          <span className="text-navy">Email all partners about this replay</span>
        </label>
      </div>

      {replays.length > 0 && (
        <div className="mt-6 border-t border-linen-border pt-4 space-y-1.5" data-testid="replay-list">
          {replays.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg bg-linen/70 px-3 py-2 text-[13px]">
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.kind === "meeting" ? "bg-navy text-white" : "bg-teal text-white"}`}>
                {r.kind === "meeting" ? "Meeting" : "Training"}
              </span>
              <span className="font-medium text-navy truncate">{r.title}</span>
              {r.recorded_on && <span className="text-ink-400 text-[11px]">{r.recorded_on}</span>}
              <button onClick={() => remove(r.id)} data-testid={`replay-delete-${r.id}`} className="ml-auto text-ink-400 hover:text-red-600 p-1">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Weekly digest card ---------------- */
function DigestCard() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/digest/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-now");
      const c = data.counts || {};
      toast.success(`Digest sent to ${data.sent_to} — ${c.leads ?? 0} leads, ${c.bdm_apps ?? 0} applications, ${c.opens ?? 0} packet opens`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl bg-navy text-white p-6 relative overflow-hidden" data-testid="comms-digest">
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-lime/15 blur-3xl" aria-hidden />
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className="text-lime" />
        <h3 className="font-heading font-medium text-lg">Weekly Digest</h3>
      </div>
      <p className="mt-1 text-[13px] text-white/70">
        Every <strong className="text-white">Monday at 7:00 AM Pacific</strong>, a summary of the past week's new leads,
        applications, inquiries, and packet opens is emailed to <strong className="text-lime">{status?.recipient || "…"}</strong>. Automatic — nothing to do.
      </p>
      {status?.last_sent_at && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-white/60" data-testid="digest-last-sent">
          <CheckCircle2 size={13} className="text-lime" /> Last sent {status.last_sent_at.slice(0, 16).replace("T", " ")} UTC ({status.last_trigger || "scheduled"})
        </div>
      )}
      <div className="mt-4">
        <button onClick={sendNow} disabled={sending} data-testid="digest-send-now" className="inline-flex items-center gap-2 rounded-full bg-lime hover:bg-lime/90 disabled:opacity-50 text-navy-deep font-semibold px-5 py-2.5 text-sm transition-colors">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Send me a digest now
        </button>
      </div>
    </div>
  );
}

/* ---------------- Monthly packet settings ---------------- */
function PacketSettingsCard() {
  const [form, setForm] = useState({ directive: "", theme: "", overview: "", first_action: "", contact_name: "", contact_email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/packet-settings").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/packet-settings", form);
      toast.success("Packet settings saved — live on all shared packets.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-packet-settings">
      <div className="flex items-center gap-2">
        <Mail size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Monthly Packet Framing</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">
        The overview, theme, and first action shown at the top of every shared asset packet — plus the
        "Questions?" contact at the bottom. Update once a month; applies to all live share links instantly.
      </p>
      <label className="block mt-5">
        <span className={labelCls}>Directive line (bold, in the packet header)</span>
        <input data-testid="pk-directive" value={form.directive} onChange={set("directive")} placeholder="These assets are for one thing: starting conversations." className={inputCls} />
        <span className="text-[11px] text-ink-500">Leave any field blank to use the built-in default shown as placeholder.</span>
      </label>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>This month's theme</span>
          <input data-testid="pk-theme" value={form.theme} onChange={set("theme")} placeholder="Before you lock, check it." className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>First action (the one thing to do first)</span>
          <input data-testid="pk-action" value={form.first_action} onChange={set("first_action")} placeholder="Send this month's script to three people this week." className={inputCls} />
        </label>
      </div>
      <label className="block mt-4">
        <span className={labelCls}>Overview (1–2 sentences)</span>
        <textarea data-testid="pk-overview" value={form.overview} onChange={set("overview")} rows={2} placeholder="Post one social this week. Send one script to three agents. Don't study the packet — use it." className={inputCls} />
      </label>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Default "Questions?" contact name</span>
          <input data-testid="pk-contact-name" value={form.contact_name} onChange={set("contact_name")} placeholder="The Great Rate Check Program Team" className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Contact email</span>
          <input data-testid="pk-contact-email" value={form.contact_email} onChange={set("contact_email")} placeholder="daren@greatratecheck.com" className={inputCls} />
        </label>
      </div>
      <p className="mt-2 text-[11px] text-ink-500">
        Hands-on MLOs (checkbox in their profile) show their own name/email instead of this default on packets they issued.
      </p>
      <button onClick={save} disabled={busy} data-testid="pk-save" className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal hover:bg-teal-hover disabled:opacity-50 text-white font-semibold px-5 py-2.5 text-sm transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save packet framing
      </button>
    </div>
  );
}

/* ---------------- Monthly meeting + reminder ---------------- */
function MeetingCard() {
  const [form, setForm] = useState({ title: "Partner Growth Meeting", date: "", time: "", join_url: "", agenda: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/meeting").then((r) => {
      if (r.data?.date) {
        setForm({
          title: r.data.title || "Partner Growth Meeting",
          date: r.data.date || "",
          time: r.data.time || "",
          join_url: r.data.join_url || "",
          agenda: r.data.agenda || "",
        });
      }
      setStatus(r.data || {});
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.date || !form.time || !form.join_url.trim()) {
      toast.error("Date, time, and join link are required.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/meeting", form);
      toast.success("Meeting saved — partners get a reminder 24 hours before.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/meeting/send-reminder-now");
      toast.success(`Reminder sent to ${data.notified} partner${data.notified !== 1 ? "s" : ""}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-meeting">
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Monthly Meeting Reminder</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">
        Save the next live meeting — all partners automatically get a branded reminder email with the
        agenda and join link <strong>24 hours before</strong> it starts.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Meeting title</span>
          <input data-testid="mt-title" value={form.title} onChange={set("title")} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Join link (Zoom / Teams) *</span>
          <input data-testid="mt-link" value={form.join_url} onChange={set("join_url")} placeholder="https://zoom.us/j/…" className={inputCls} />
        </label>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 md:max-w-md">
        <label className="block">
          <span className={labelCls}>Date *</span>
          <input data-testid="mt-date" type="date" value={form.date} onChange={set("date")} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Time (Pacific) *</span>
          <input data-testid="mt-time" type="time" value={form.time} onChange={set("time")} className={inputCls} />
        </label>
      </div>
      <label className="block mt-4">
        <span className={labelCls}>Agenda (one item per line)</span>
        <textarea data-testid="mt-agenda" value={form.agenda} onChange={set("agenda")} rows={4} placeholder={"Program update & new partners\nThis month's marketing theme\nGrowth tactic: approaching brokerages\nWins + Top Partner of the Month"} className={inputCls} />
      </label>

      {status?.date && (
        <div className="mt-3 text-[12px] text-ink-500" data-testid="mt-status">
          Scheduled: <strong className="text-navy">{status.date} at {status.time} PT</strong>
          {status.reminder_sent
            ? <span className="ml-2 inline-flex items-center gap-1 text-teal"><CheckCircle2 size={12} /> reminder sent{status.reminder_sent_at ? ` (${status.reminder_sent_at.slice(0, 16).replace("T", " ")} UTC)` : ""}</span>
            : <span className="ml-2 text-ink-400">reminder pending — goes out 24h before</span>}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="mt-save" className="inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold px-5 py-2.5 text-sm transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarClock size={15} />} Save meeting
        </button>
        <button onClick={sendNow} disabled={sending || !status?.date} data-testid="mt-send-now" className="inline-flex items-center gap-2 rounded-full border border-teal/40 text-teal hover:bg-teal-light/50 disabled:opacity-50 font-semibold px-5 py-2.5 text-sm transition-colors">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Send reminder now
        </button>
      </div>
    </div>
  );
}

/* ---------------- Issue this month's packet ---------------- */
function PacketIssueCard() {
  const [subs, setSubs] = useState([]);
  const [subId, setSubId] = useState("");
  const [recips, setRecips] = useState([]);
  const [checked, setChecked] = useState({});
  const [showRecips, setShowRecips] = useState(false);
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [excludedCount, setExcludedCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/library/subcategories"),
      api.get("/admin/field-updates/recipients"),
      api.get("/admin/packet-settings"),
    ]).then(([s, r, p]) => {
      const list = s.data || [];
      setSubs(list);
      const saved = p.data?.packet_subcategory_id;
      const def = (saved && list.find((x) => x.id === saved)) ? saved
        : (list.find((x) => x.category === "marketing") || list[0])?.id || "";
      setSubId(def);
      setRecips(r.data || []);
      const all = {};
      (r.data || []).forEach((x) => { all[x.email] = true; });
      setChecked(all);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!subId) return;
    api.get(`/library/admin/subcategories/${subId}/uncategorized-count`)
      .then((r) => setExcludedCount(r.data?.uncategorized_count || 0))
      .catch(() => setExcludedCount(0));
  }, [subId]);

  const selected = useMemo(() => recips.filter((r) => checked[r.email]).map((r) => r.email), [recips, checked]);

  const defaultSubject = useMemo(() => {
    const s = subs.find((x) => x.id === subId);
    return s ? `Great Rate Check — ${s.share_name || s.name}` : "";
  }, [subs, subId]);

  useEffect(() => {
    setSubject(defaultSubject);
  }, [defaultSubject]);

  const issue = async () => {
    if (!subId) { toast.error("Pick which library section is this month's packet."); return; }
    if (selected.length === 0) { toast.error("Pick at least one recipient."); return; }
    setBusy(true);
    try {
      await api.post(`/library/admin/subcategories/${subId}/issue`, { recipients: selected, note: note.trim(), subject: subject.trim() });
      const settings = await api.get("/admin/packet-settings");
      await api.put("/admin/packet-settings", { ...settings.data, packet_subcategory_id: subId });
      toast.success(`Packet issued to ${selected.length} partner${selected.length !== 1 ? "s" : ""} — tracked in Sends.`);
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-packet-issue">
      <div className="flex items-center gap-2">
        <Send size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Issue This Month's Packet</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">
        One send to every partner — they get the branded email with a tracked link to the packet page
        (framing above included automatically). Only files inside folders go out.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Which section is the packet?</span>
          <select data-testid="pi-subcategory" value={subId} onChange={(e) => setSubId(e.target.value)} className={inputCls}>
            {subs.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.category})</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Note in the email (optional)</span>
          <input data-testid="pi-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="June packet is live — start with the new script." className={inputCls} />
        </label>
      </div>

      <label className="block mt-4">
        <span className={labelCls}>Email subject</span>
        <input data-testid="pi-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={defaultSubject || "Great Rate Check — Current Asset Packet"} className={inputCls} />
        <span className="mt-1 block text-[11px] text-ink-500">
          Defaults to the section's partner-facing name. Make it plain-English so recipients know exactly what they're getting.
        </span>
      </label>

      {excludedCount > 0 && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="pi-excluded-note">
          {excludedCount} uncategorized file{excludedCount !== 1 ? "s" : ""} won't be included — only files inside folders go out.
        </div>
      )}

      <div className="mt-4 rounded-xl border border-linen-border bg-linen/60 p-4">
        <button onClick={() => setShowRecips((s) => !s)} data-testid="pi-recipients-toggle" className="w-full flex items-center justify-between text-left">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-navy">
            <Users size={15} className="text-teal" /> Recipients: {selected.length} of {recips.length} selected
          </span>
          {showRecips ? <ChevronUp size={16} className="text-ink-500" /> : <ChevronDown size={16} className="text-ink-500" />}
        </button>
        {showRecips && (
          <div className="mt-3">
            <div className="flex gap-3 text-xs mb-2">
              <button onClick={() => { const a = {}; recips.forEach((r) => { a[r.email] = true; }); setChecked(a); }} className="text-teal font-medium">Select all</button>
              <button onClick={() => setChecked({})} className="text-ink-500">Clear</button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
              {recips.map((r) => (
                <label key={r.email} className="flex items-center gap-2.5 rounded-lg bg-white border border-linen-border px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!checked[r.email]} onChange={() => setChecked((c) => ({ ...c, [r.email]: !c[r.email] }))} className="accent-[#1F8F8B]" />
                  <span className="text-sm text-navy">{r.name || r.email}</span>
                  <span className="text-[11px] text-ink-400">{r.email}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.role === "MLO" ? "bg-teal-light text-teal" : "bg-lime-soft text-navy"}`}>{r.role}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <button onClick={issue} disabled={busy} data-testid="pi-issue" className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold px-6 py-3 transition-colors">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        Issue packet to {selected.length} partner{selected.length !== 1 ? "s" : ""}
      </button>
    </div>
  );
}

/* ---------------- Partner community channel ---------------- */
function CommunitySettingsCard() {
  const [form, setForm] = useState({ enabled: false, platform: "whatsapp", label: "", url: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/community").then((r) => {
      const d = r.data || {};
      setForm({
        enabled: !!d.enabled,
        platform: d.platform || "whatsapp",
        label: d.label || "",
        url: d.url || "",
        note: d.note || "",
      });
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/community", form);
      setForm({ enabled: !!data.enabled, platform: data.platform, label: data.label, url: data.url, note: data.note });
      toast.success(data.enabled ? "Partner group is live — it now shows on dashboards and in every Field Update." : "Saved. The group stays hidden until you switch it on.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-community">
      <div className="flex items-center gap-2">
        <MessagesSquare size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">The Partner Group</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">
        One invite link, surfaced in three places automatically: every MLO dashboard, the bottom of every
        Field Update email, and the partner onboarding page.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Platform</span>
          <select data-testid="community-platform" value={form.platform} onChange={set("platform")} className={inputCls}>
            <option value="whatsapp">WhatsApp group</option>
            <option value="slack">Slack workspace</option>
            <option value="telegram">Telegram group</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Group name partners see</span>
          <input data-testid="community-label" value={form.label} onChange={set("label")} placeholder="Great Rate Check Partner Group" className={inputCls} />
        </label>
      </div>
      <label className="block mt-4">
        <span className={labelCls}>Invite link</span>
        <input data-testid="community-url" value={form.url} onChange={set("url")} placeholder="https://chat.whatsapp.com/…" className={inputCls} />
        <span className="mt-1 block text-[11px] text-ink-500">
          In WhatsApp: open the group → Group info → Invite via link → Copy link.
        </span>
      </label>
      <label className="block mt-4">
        <span className={labelCls}>One-line description</span>
        <input data-testid="community-note" value={form.note} onChange={set("note")} placeholder="Wins, questions, and quick answers between meetings. Partners only." className={inputCls} />
      </label>

      <label className="mt-4 flex items-center gap-2.5 text-sm text-navy cursor-pointer">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          data-testid="community-enabled"
          className="accent-[#1F8F8B]"
        />
        Show the group to partners
      </label>

      <button onClick={save} disabled={busy} data-testid="community-save" className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold px-6 py-2.5 text-sm transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save group
      </button>
    </div>
  );
}

/* ---------------- Setup nudges (MLO onboarding chase) ---------------- */
function SetupNudgesCard() {
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nudging, setNudging] = useState("");

  const load = useCallback(() => {
    api.get("/admin/onboarding/nudge-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/onboarding/mlos").then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/onboarding/nudge-settings", { ...settings, ...patch });
      setSettings(data);
      toast.success(data.enabled ? "Weekly setup reminders are on." : "Setup reminders paused — nobody gets emailed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const nudge = async (slug) => {
    setNudging(slug);
    try {
      const { data } = await api.post(`/admin/onboarding/mlos/${slug}/nudge`);
      toast.success(`Reminder sent — ${data.missing} step${data.missing !== 1 ? "s" : ""} left${data.admin_copied ? " (you were copied)" : ""}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
    } finally {
      setNudging("");
    }
  };

  const stalled = rows.filter((r) => r.has_account && !r.complete);

  if (!settings) return null;

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="comms-setup-nudges">
      <div className="flex items-center gap-2">
        <BellRing size={18} className="text-teal" />
        <h3 className="font-heading font-medium text-lg text-navy">Setup Nudges</h3>
      </div>
      <p className="mt-1 text-[13px] text-ink-500">
        Officers whose checklist is still unfinished get a weekly email naming the exact step they're
        stuck on. From the {settings.cc_after === 1 ? "second" : "third"} reminder you get a copy, so
        you know when to pick up the phone instead.
      </p>

      <label className="mt-4 flex items-center gap-2.5 text-sm text-navy cursor-pointer">
        <input
          type="checkbox"
          checked={settings.enabled}
          disabled={busy}
          onChange={(e) => save({ enabled: e.target.checked })}
          data-testid="nudge-enabled"
          className="accent-[#1F8F8B]"
        />
        Send weekly reminders automatically
      </label>
      {!settings.enabled && (
        <p className="mt-1.5 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="nudge-off-note">
          Currently paused. Turning this on will start emailing the {stalled.length} officer{stalled.length !== 1 ? "s" : ""} below on the next daily sweep.
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block">
          <span className={labelCls}>Start after (days)</span>
          <input
            type="number" min={0} max={90}
            value={settings.start_after_days}
            onChange={(e) => setSettings((s) => ({ ...s, start_after_days: Number(e.target.value) }))}
            onBlur={() => save({})}
            data-testid="nudge-start-after"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Then every (days)</span>
          <input
            type="number" min={1} max={60}
            value={settings.cadence_days}
            onChange={(e) => setSettings((s) => ({ ...s, cadence_days: Number(e.target.value) }))}
            onBlur={() => save({})}
            data-testid="nudge-cadence"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Copy me after N unanswered</span>
          <input
            type="number" min={0} max={10}
            value={settings.cc_after}
            onChange={(e) => setSettings((s) => ({ ...s, cc_after: Number(e.target.value) }))}
            onBlur={() => save({})}
            data-testid="nudge-cc-after"
            className={inputCls}
          />
        </label>
      </div>

      <div className="mt-5">
        <div className={eyebrowCls}>Officer setup</div>
        <div className="mt-2 space-y-1.5" data-testid="nudge-roster">
          {rows.length === 0 && <div className="text-xs text-ink-400 py-2">No officers yet.</div>}
          {rows.map((r) => (
            <div
              key={r.slug}
              className="flex flex-wrap items-center gap-2.5 rounded-xl bg-linen/70 px-3 py-2.5"
              data-testid={`nudge-row-${r.slug}`}
            >
              <span className="w-10 shrink-0 text-right text-sm font-semibold text-navy tabular-nums">{r.percent}%</span>
              <span className="w-24 sm:w-32 h-1.5 rounded-full bg-white overflow-hidden shrink-0">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${r.percent}%`, background: r.complete ? "#1F8F8B" : "#A6D24A", transition: "width .4s ease" }}
                />
              </span>
              <span className="text-sm font-medium text-navy">{r.name || r.slug}</span>
              {r.demo && <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-white border border-linen-border text-ink-400">Demo</span>}
              {!r.has_account && <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-white border border-linen-border text-ink-400">No login</span>}
              {r.complete ? (
                <span className="text-xs text-teal font-medium inline-flex items-center gap-1"><CheckCircle2 size={12} /> Done</span>
              ) : (
                <span className="text-xs text-ink-500">Stuck on: <span className="text-navy">{r.stuck_label}</span></span>
              )}
              <span className="ml-auto flex items-center gap-3">
                {r.nudge_count > 0 && (
                  <span className="text-[11px] text-ink-400">
                    {r.nudge_count} reminder{r.nudge_count !== 1 ? "s" : ""} · {(r.last_nudge_at || "").slice(0, 10)}
                  </span>
                )}
                {!r.complete && r.has_account && (
                  <button
                    onClick={() => nudge(r.slug)}
                    disabled={nudging === r.slug}
                    data-testid={`nudge-send-${r.slug}`}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-navy text-white px-3 py-1.5 rounded-full hover:bg-navy-light disabled:opacity-50 transition-colors"
                  >
                    {nudging === r.slug ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Nudge now
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const CommsPanel = () => (  <div className="space-y-5" data-testid="admin-comms-panel">
    <DigestCard />
    <CommunitySettingsCard />
    <SetupNudgesCard />
    <MeetingCard />
    <FieldUpdateComposer />
    <PacketSettingsCard />
    <PacketIssueCard />
    <ReplayManager />
  </div>
);
